import React, { useMemo } from 'react'
import { TrendingUp, Loader2 } from 'lucide-react'
import { ThemePreset } from '../../theme/presets'

interface ChartPoint {
  date: string
  value: number
}

interface LineChartProps {
  data: ChartPoint[]
  homologousData?: ChartPoint[]
  loading: boolean
  palette: ThemePreset
}

const fmtMoney = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(value)

const fmtDateShort = (value: string): string => {
  try {
    const d = new Date(value + 'T00:00:00')
    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(d)
  } catch {
    return value
  }
}

function yOf(y: number, yMin: number, yMax: number, innerH: number, padT: number) {
  const ySpan = yMax - yMin || 1
  return padT + (1 - (y - yMin) / ySpan) * innerH
}

function buildPath(
  data: ChartPoint[],
  xOf: (i: number) => number,
  yMin: number,
  yMax: number,
  innerH: number,
  padT: number
): string {
  return data
    .map((p, i) => {
      const prefix = i === 0 ? 'M' : 'L'
      return `${prefix} ${xOf(i).toFixed(2)} ${yOf(p.value, yMin, yMax, innerH, padT).toFixed(2)}`
    })
    .join(' ')
}

export const LineChart: React.FC<LineChartProps> = ({ data, loading, palette, homologousData }) => {
  const hasHomologous = !!(homologousData && homologousData.length > 0)

  const svgContent = useMemo(() => {
    if (!data.length) return null

    const W = 1000
    const H = 300
    const padL = 72
    const padR = 24
    const padT = 30
    const padB = 40
    const innerW = W - padL - padR
    const innerH = H - padT - padB

    // Combine both datasets for scale
    const allValues = [...data.map((p) => p.value)]
    if (hasHomologous) {
      allValues.push(...homologousData!.map((p) => p.value))
    }
    const yMin = 0
    const yMax = Math.max(...allValues) * 1.1 || 1

    // X scale based on primary data
    const maxPoints = Math.max(data.length, hasHomologous ? homologousData!.length : 0)
    const xStep = innerW / Math.max(maxPoints - 1, 1)
    const xOf = (i: number) => padL + i * xStep

    // Grid lines
    const gridCount = 5
    const ySpan = yMax - yMin || 1
    const gridLines = []
    for (let i = 0; i <= gridCount; i++) {
      const y = padT + (i / gridCount) * innerH
      const val = yMax - (i / gridCount) * ySpan
      gridLines.push(
        <g key={`grid-${i}`}>
          <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e2e8f0" strokeWidth="1" />
          <text x={padL - 8} y={y + 4} fill="#94a3b8" fontSize="11" textAnchor="end">
            {fmtMoney(val)}
          </text>
        </g>
      )
    }

    // X-axis labels
    const labelInterval = Math.max(1, Math.floor(data.length / 8))
    const xLabels = data
      .filter((_, idx) => idx % labelInterval === 0 || idx === data.length - 1)

    // Area fill (primary)
    const areaD =
      buildPath(data, xOf, yMin, yMax, innerH, padT) +
      ` L ${xOf(data.length - 1).toFixed(2)} ${yOf(yMin, yMin, yMax, innerH, padT).toFixed(2)}` +
      ` L ${xOf(0).toFixed(2)} ${yOf(yMin, yMin, yMax, innerH, padT).toFixed(2)} Z`

    // Line paths
    const lineD = buildPath(data, xOf, yMin, yMax, innerH, padT)

    // Primary dots
    const dots = data.map((p, i) => (
      <circle
        key={`dot-${i}`}
        cx={xOf(i).toFixed(2)}
        cy={yOf(p.value, yMin, yMax, innerH, padT).toFixed(2)}
        r="2.5"
        fill={palette.primary}
        opacity="0.7"
      />
    ))

    // Homologous line (dashed, secondary color)
    let homologousLine: React.ReactNode = null
    let homologousDots: React.ReactNode = null
    if (hasHomologous) {
      const homoD = buildPath(homologousData!, xOf, yMin, yMax, innerH, padT)
      homologousLine = (
        <path
          d={homoD}
          fill="none"
          stroke="#94a3b8"
          strokeWidth="2"
          strokeDasharray="6 4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )
      homologousDots = homologousData!.map((p, i) => (
        <circle
          key={`hdot-${i}`}
          cx={xOf(i).toFixed(2)}
          cy={yOf(p.value, yMin, yMax, innerH, padT).toFixed(2)}
          r="2"
          fill="#94a3b8"
          opacity="0.6"
        />
      ))
    }

    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        {gridLines}
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={palette.primary} stopOpacity="0.18" />
            <stop offset="100%" stopColor={palette.primary} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#areaGrad)" />
        <path d={lineD} fill="none" stroke={palette.primary} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {dots}
        {homologousLine}
        {homologousDots}
        {xLabels.map(({ date }, i) => {
          const idx = i * labelInterval
          if (idx >= data.length) return null
          return (
            <text
              key={`xlabel-${idx}`}
              x={xOf(idx).toFixed(2)}
              y={H - padB + 16}
              fill="#94a3b8"
              fontSize="10"
              textAnchor="middle"
            >
              {fmtDateShort(date)}
            </text>
          )
        })}
        {/* Legend */}
        {hasHomologous && (
          <g transform={`translate(${W - padR - 220}, ${padT - 12})`}>
            <line x1="0" y1="0" x2="16" y2="0" stroke={palette.primary} strokeWidth="2.5" />
            <text x="22" y="4" fill="#64748b" fontSize="11">Atual</text>
            <line x1="60" y1="0" x2="76" y2="0" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 4" />
            <text x="82" y="4" fill="#94a3b8" fontSize="11">Ano anterior</text>
          </g>
        )}
      </svg>
    )
  }, [data, palette, hasHomologous, homologousData])

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5" style={{ color: palette.primary }} />
        <h3 className="text-base font-semibold text-slate-800 dark:text-white">
          Evolução Diária de Vendas
        </h3>
        {hasHomologous && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200 font-medium ml-1">
            Homólogo
          </span>
        )}
        {!loading && data.length > 0 && (
          <span className="text-xs text-slate-400 dark:text-slate-500 ml-auto">
            {data.length} dias
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[300px]">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : !data.length ? (
        <div className="flex items-center justify-center h-[300px] text-slate-400 text-sm">
          Selecione um período e clique em Aplicar para visualizar o gráfico.
        </div>
      ) : (
        <div className="h-[310px] w-full">
          {svgContent}
        </div>
      )}
    </div>
  )
}
